import { useState, useCallback } from 'react';

interface UseInlineEditOptions {
  initialValue: string;
  onSave: (value: string) => Promise<void>;
  onCancel?: () => void;
}

interface UseInlineEditReturn {
  isEditing: boolean;
  editValue: string;
  startEditing: (value?: string) => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  handleChange: (value: string) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Custom hook for managing inline editing state and behavior.
 * Provides consistent editing experience across components with save/cancel/keyboard handling.
 */
export function useInlineEdit({
  initialValue,
  onSave,
  onCancel,
}: UseInlineEditOptions): UseInlineEditReturn {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(initialValue);

  const startEditing = useCallback(
    (value?: string) => {
      setIsEditing(true);
      setEditValue(value ?? initialValue);
    },
    [initialValue],
  );

  const handleSave = useCallback(async () => {
    if (editValue.trim() !== initialValue) {
      await onSave(editValue.trim());
    }
    setIsEditing(false);
  }, [editValue, initialValue, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(initialValue);
    setIsEditing(false);
    onCancel?.();
  }, [initialValue, onCancel]);

  const handleChange = useCallback((value: string) => {
    setEditValue(value);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  return {
    isEditing,
    editValue,
    startEditing,
    handleSave,
    handleCancel,
    handleChange,
    handleKeyDown,
  };
}
